<template>
  <v-container fluid>
    <!-- Encabezado -->
    <v-row align="center" class="mb-1">
      <v-col>
        <h2 class="headline font-weight-bold">
          <v-icon color="purple darken-2" large class="mr-2">mdi-calendar-check</v-icon>
          Checklist de Contenidos — {{ instancia.toUpperCase() }}
        </h2>
      </v-col>
      <v-col cols="auto">
        <v-btn color="primary" class="mr-2" @click="abrirAgregarManual">
          <v-icon left>mdi-plus</v-icon>Nueva
        </v-btn>
        <input type="file" ref="fileInput" accept=".xlsx, .xls" style="display: none" @change="onFileChange" />
        <v-btn color="success" @click="$refs.fileInput.click()">
          <v-icon left>mdi-file-excel</v-icon>Importar Excel
        </v-btn>
      </v-col>
    </v-row>

    <!-- Filtros -->
    <v-card class="mb-4 pa-3">
      <v-row dense align="center">
        <v-col cols="12" md="3">
          <v-select v-model="filtros.ciclo" :items="ciclosVisibles" item-text="ciclo" item-value="id" label="Ciclo"
            outlined dense hide-details clearable @change="cargarTareas" />
        </v-col>
        <v-col cols="12" md="3">
          <v-select v-model="filtros.estado" :items="estados" item-text="nombre" item-value="id" label="Estado" outlined
            dense hide-details clearable @change="cargarTareas" />
        </v-col>
        <v-col cols="12" md="3">
          <v-select v-model="filtros.plataforma" :items="plataformas" item-text="nombre" item-value="id"
            label="Plataforma" outlined dense hide-details clearable @change="cargarTareas" />
        </v-col>
        <v-col cols="auto">
          <v-btn text small @click="limpiarFiltros"><v-icon left small>mdi-filter-off</v-icon>Limpiar</v-btn>
        </v-col>
      </v-row>
    </v-card>

    <!-- Tabla Principal -->
    <v-card>
      <v-card-text class="pa-0">
        <v-data-table :headers="headers" :items="tareas" :loading="cargando" loading-text="Cargando actividades..."
          no-data-text="Sin actividades encontradas" class="elevation-0">
          
          <template v-slot:item.descripcion="{ item }">
            <div style="max-width: 200px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;" 
                 :title="item.descripcion">
              {{ item.descripcion }}
            </div>
          </template>

          <template v-slot:item.fecha="{ item }">
            {{ formatFecha(item.fecha) }}
          </template>

          <template v-slot:item.id_estado="{ item }">
            <v-select v-model="item.id_estado" :items="estadosCalculados(item)" item-text="nombre" item-value="id" dense outlined
              hide-details class="mt-1 mb-1" style="min-width: 130px; max-width: 150px" @change="cambiarEstado(item)" />
          </template>

          <template v-slot:item.recurso="{ item }">
            <template v-if="item.id_recurso">
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn icon color="primary" v-bind="attrs" v-on="on" @click="verRecursoVinculado(item)">
                    <v-icon>mdi-eye-check</v-icon>
                  </v-btn>
                </template>
                <span>{{ item.recurso_nombre }}</span>
              </v-tooltip>

              <v-btn v-if="puestosPermitidos" icon color="red" small title="Desvincular archivo"
                @click="desvincularRecurso(item)">
                <v-icon small>mdi-link-variant-off</v-icon>
              </v-btn>
            </template>
            <template v-else>
              <v-btn small outlined color="grey darken-1" @click="abrirVincularModal(item)">
                <v-icon left small>mdi-link-plus</v-icon>Vincular
              </v-btn>
            </template>
          </template>

          <template v-slot:item.acciones="{ item }" v-if="puestosPermitidos">
            <v-btn icon small color="blue" class="mr-1" @click="abrirEdicion(item)" title="Editar">
              <v-icon small>mdi-pencil</v-icon>
            </v-btn>
            <v-btn icon small color="red" @click="eliminarTarea(item)" title="Eliminar">
              <v-icon small>mdi-trash-can-outline</v-icon>
            </v-btn>
          </template>

        </v-data-table>
      </v-card-text>
    </v-card>

    <!-- Dialogo Importar Previsualización -->
    <v-dialog v-model="dlgImportar" max-width="900" persistent>
      <v-card>
        <v-card-title>
          Confirmar Importación
        </v-card-title>
        <v-card-text>
          <v-alert type="warning" dense text v-if="importDialog.advertencia">
            El archivo contiene nomenclaturas de Plataformas, Temas, Formatos o Estados que no existen actualmente en
            los
            catálogos.
            <strong>Se crearán automáticamente al confirmar.</strong>
          </v-alert>
          <v-select v-model="importDialog.id_ciclo" :items="ciclosVisibles" item-text="ciclo" item-value="id"
            label="Selecciona a qué ciclo asignarás estas fechas *" outlined dense class="mb-4" />
          <p>Se importarán {{ importDialog.tareas.length }} actividades extraídas del Excel.</p>

          <v-data-table :headers="headersImportPreview" :items="importDialog.tareas.slice(0, 10)" disable-pagination
            hide-default-footer dense class="elevation-1">
            <template v-slot:top>
              <div class="pa-2 caption grey--text text-right">Mostrando previsualización (máx 10 filas)</div>
            </template>
            <template v-slot:item.fecha="{ item }">
              {{ formatFecha(item.fecha) }}
            </template>
          </v-data-table>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="dlgImportar = false; $refs.fileInput.value = null">Cancelar</v-btn>
          <v-btn color="success" :disabled="!importDialog.id_ciclo" :loading="guardando" @click="ejecutarImportacion">
            Importar Actividades
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialogo Edicion / Alta Manual -->
    <v-dialog v-model="dlgEdit" max-width="600" persistent>
      <v-card v-if="itemEdit || esNuevaTarea">
        <v-card-title>{{ esNuevaTarea ? 'Agregar Nueva Actividad' : 'Editar Actividad' }}</v-card-title>
        <v-card-text>
          <v-form ref="formEdicion">
            <v-row dense>
              <v-col cols="6" v-if="esNuevaTarea">
                <v-select v-model="formModel.id_ciclo" :items="ciclosVisibles" item-text="ciclo" item-value="id"
                  label="Ciclo" outlined dense />
              </v-col>
              <v-col :cols="esNuevaTarea ? 6 : 12">
                <v-text-field v-model="formModel.fecha" type="date" label="Fecha" outlined dense />
              </v-col>
              <v-col cols="6">
                <v-select v-model="formModel.id_plataforma" :items="plataformas" item-text="nombre" item-value="id"
                  label="Plataforma" outlined dense />
              </v-col>
              <v-col cols="6">
                <v-select v-model="formModel.id_tipo" :items="formatos" item-text="nombre" item-value="id"
                  label="Formato" outlined dense />
              </v-col>
              <v-col cols="6">
                <v-select v-model="formModel.id_tema" :items="temas" item-text="nombre" item-value="id" label="Tema"
                  outlined dense />
              </v-col>
              <v-col cols="6" v-if="esNuevaTarea">
                <v-select v-model="formModel.id_estado" :items="estados" item-text="nombre" item-value="id"
                  label="Estado Inicial" outlined dense />
              </v-col>
              <v-col cols="12">
                <v-textarea v-model="formModel.descripcion" label="Descripción del Post / Tarea" outlined dense rows="3" />
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="dlgEdit = false">Cancelar</v-btn>
          <v-btn color="primary" :loading="guardando" @click="guardarEdicion">Guardar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialogo Vincular Recurso Multimedia -->
    <v-dialog v-model="dlgVincular" max-width="700">
      <v-card>
        <v-card-title>Vincular a Recurso Multimedia</v-card-title>
        <v-card-text>
          <p>Selecciona el recurso multimedia subido previamente para asociarlo a esta actividad.</p>
          <v-autocomplete v-model="recursoSeleccionado" :items="recursosLocales" item-text="nombre" item-value="id"
            label="Buscar recurso por nombre" outlined dense clearable prepend-inner-icon="mdi-magnify">
            <template v-slot:item="{ item }">
              <v-list-item-content>
                <v-list-item-title>{{ item.nombre }}</v-list-item-title>
                <v-list-item-subtitle>{{ item.tipo_nombre }} - {{ item.uso_nombre }}</v-list-item-subtitle>
              </v-list-item-content>
            </template>
          </v-autocomplete>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="dlgVincular = false">Cancelar</v-btn>
          <v-btn color="primary" :disabled="!recursoSeleccionado" :loading="guardando" @click="confirmarVinculacion">
            Vincular Selección
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialogo Visor de Archivos Vinculados (Idéntico a Multimedia) -->
    <v-dialog v-model="dlgVisor" max-width="800">
      <v-card v-if="recursoVisor">
        <v-card-title class="pa-3 justify-space-between">
          <span class="subtitle-1 font-weight-bold">{{ recursoVisor.recurso_nombre }}</span>
          <v-btn icon @click="dlgVisor = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-divider />
        <v-card-text class="pa-0 bg-grey-lighten-4 text-center d-flex align-center justify-center"
          style="min-height: 300px;">
          <v-img v-if="esImagen(recursoVisor.recurso_url)" :src="getUrl(recursoVisor.recurso_url)" contain
            max-height="70vh" />
          <video v-else-if="!esPdf(recursoVisor.recurso_url)" controls autoplay
            style="width: 100%; max-height: 70vh; background: black;">
            <source :src="getUrl(recursoVisor.recurso_url)" type="video/mp4" />
          </video>
          <v-sheet v-else height="300" class="d-flex flex-column align-center justify-center w-100">
            <v-icon size="80" color="red" class="mb-4">mdi-file-pdf-box</v-icon>
            <v-btn color="red" dark @click="abrirArchivoPestana(recursoVisor.recurso_url)">Abrir PDF</v-btn>
          </v-sheet>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snack.show" :color="snack.color" top right timeout="4000">{{ snack.text }}</v-snackbar>
  </v-container>
</template>

<script>
import { mapGetters } from 'vuex/dist/vuex.common.js';
import XLSX from 'xlsx';
import moment from 'moment';

export default {
  name: 'ChecklistTareas',
  props: {
    instancia: { type: String, required: true }
  },
  watch: {
    instancia() {
      this.cargarCatalogosEInit();
    }
  },
  data() {
    return {
      tareas: [], cargando: false, guardando: false,

      ciclosList: [], 
      plataformas: [], 
      formatos: [], 
      temas: [],
      descripciones: [], 
      estados: [],
      filtros: { 
        ciclo: null, 
        estado: null, 
        plataforma: null 
      },

      // Importacion
      dlgImportar: false,
      importDialog: { 
        tareas: [], 
        advertencia: false, 
        id_ciclo: null, 
        jsonExcelTemp: [] 
      },

      // Alta manual y Edicion (Se combinan en formData)
      dlgEdit: false, 
      esNuevaTarea: false, 
      itemEdit: null,
      formModel: { 
        id_ciclo: null, 
        fecha: '', 
        id_plataforma: null, 
        id_tipo: null, 
        id_tema: null, 
        descripcion: '',
        id_estado: null 
      },

      // Vinculacion
      dlgVincular: false,
      tareaVincular: null, 
      recursoSeleccionado: null, 
      recursosLocales: [],

      // Visor
      dlgVisor: false, recursoVisor: null,

      headers: [
        { text: 'Fecha', value: 'fecha', width: 105 },
        { text: 'Plataforma', value: 'plataforma_nombre' },
        { text: 'Formato', value: 'formato_nombre' },
        { text: 'Tema', value: 'tema_nombre' },
        { text: 'Descripción', value: 'descripcion' },
        { text: 'Estado', value: 'id_estado', width: 160 },
        { text: 'Recurso', value: 'recurso', width: 130, sortable: false, align: 'center' },
      ],
      headersImportPreview: [
        { text: 'Fecha', value: 'fecha' },
        { text: 'Plataforma', value: 'plataforma_nombre' },
        { text: 'Formato', value: 'formato_nombre' },
        { text: 'Tema', value: 'tema_nombre' },
        { text: 'Estado', value: 'estado_nombre' },
      ],
      snack: { show: false, text: '', color: 'success' },
    };
  },
  computed: {
    ...mapGetters('login', ['getdatosUsuarioERPERP']),
    idUsuario() { return (this.getdatosUsuarioERPERP && this.getdatosUsuarioERPERP.iderp) ? this.getdatosUsuarioERPERP.iderp : 1; },
    puestosPermitidos() {
      const puesto = (this.getdatosUsuarioERPERP && this.getdatosUsuarioERPERP.idpuesto) ? this.getdatosUsuarioERPERP.idpuesto : 0;
      return [12, 48].includes(Number(puesto)) || [2817].includes(Number(this.getdatosUsuarioERPERP.iderp));
    },
    ciclosVisibles() { return this.ciclosList.filter(c => c.visible); }
  },
  mounted() {
    this.cargarCatalogosEInit();
  },
  methods: {
    showSnack(text, color = 'success') {
      this.snack.text = text; this.snack.color = color; this.snack.show = true;
    },
    formatFecha(fecha) {
      if (!fecha) return '';
      return moment(fecha).format('DD MMM YYYY');
    },
    esImagen(url) { return /\.(jpg|jpeg|png)$/i.test(url); },
    esPdf(url) { return /\.pdf$/i.test(url); },
    getUrl(url) { return (this.$http.defaults && this.$http.defaults.baseURL ? this.$http.defaults.baseURL.replace(/\/$/, '') : '') + url; },
    abrirArchivoPestana(url) { window.open(this.getUrl(url), '_blank'); },

    estadosCalculados(item) {
      if (item.id_recurso) return this.estados;
      return this.estados.filter(e => e.nombre.toLowerCase() !== 'listo');
    },

    async cargarCatalogosEInit() {
      try {
        const [rC, rP, rF, rT, rE] = await Promise.all([
          this.$http.get(`multimedia.ciclos.list?inst=${this.instancia}`),
          this.$http.get(`checklist.catalogos.list/plataformas?inst=${this.instancia}&activos=true`),
          this.$http.get(`multimedia.tipos.list?inst=${this.instancia}`), // Usamos multimedia_tipos para formatos
          this.$http.get(`checklist.catalogos.list/temas?inst=${this.instancia}&activos=true`),
          this.$http.get(`checklist.catalogos.list/estados?inst=${this.instancia}&activos=true`)
        ]);

        this.ciclosList = rC.data.map(c => ({ ...c, visible: Number(c.visible) }));
        this.plataformas = rP.data.data;
        this.formatos = rF.data;     // De multimedia
        this.temas = rT.data.data;
        this.estados = rE.data.data;

        if (this.ciclosVisibles.length) {
          this.filtros.ciclo = this.ciclosVisibles[0].id_ciclo;
        }

        if (this.puestosPermitidos) {
          if (!this.headers.find(h => h.value === 'acciones')) {
            this.headers.push({ text: 'Acciones', value: 'acciones', width: 90, sortable: false });
          }
        }

        this.cargarTareas();
      } catch (e) {
        this.showSnack('Error cargando catálogos iniciales', 'error');
      }
    },

    limpiarFiltros() {
      this.filtros = { ciclo: null, estado: null, plataforma: null };
      this.cargarTareas();
    },

    async cargarTareas() {
      this.cargando = true;
      try {
        const p = new URLSearchParams();
        p.append('inst', this.instancia);
        if (this.filtros.ciclo) p.append('ciclo', this.filtros.ciclo);
        if (this.filtros.estado) p.append('estado', this.filtros.estado);
        if (this.filtros.plataforma) p.append('plataforma', this.filtros.plataforma);

        const res = await this.$http.get(`checklist.tareas.list?${p.toString()}`);

        // Mapear la fecha para compatibilidad con edits (YYYY-MM-DD)
        this.tareas = res.data.data.map(t => ({
          ...t,
          fecha: t.fecha ? moment(t.fecha).format('YYYY-MM-DD') : null
        }));
      } catch (err) {
        this.showSnack('Error al cargar actividades', 'error');
      } finally {
        this.cargando = false;
      }
    },

    // ==========================================
    // CAMBIO DE ESTADO (V-SELECT INLINE)
    // ==========================================
    async cambiarEstado(item) {
      try {
        await this.$http.put(`checklist.tareas.estado/${item.id}`, {
          id_estado: item.id_estado,
          id_usuario: this.idUsuario,
          instancia: this.instancia
        });
        this.showSnack('Estado actualizado');
      } catch (err) {
        this.showSnack(err.response.data.message || 'Error al actualizar estado', 'error');
        this.cargarTareas(); // Refrescar para devolver el v-select a la normalidad si fallo
      }
    },

    // ==========================================
    // IMPORTACIÓN DESDE EXCEL
    // ==========================================
    onFileChange(e) {
      const file = e.target.files[0];
      if (!file) return;

      const reader = new FileReader();
      reader.onload = (evt) => {
        try {
          const data = new Uint8Array(evt.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          const firstSheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[firstSheetName];
          const json = XLSX.utils.sheet_to_json(worksheet, { defval: '' });

          if (json.length === 0) {
            this.showSnack('El archivo está vacío', 'error');
            this.$refs.fileInput.value = null;
            return;
          }

          this.importDialog.jsonExcelTemp = json;
          this.procesarJSONExcel(json);
        } catch (error) {
          console.error('[Excel Error]', error);
          this.showSnack('Error al procesar el archivo Excel. Verifica el formato.', 'error');
        } finally {
          // Limpiar input para permitir seleccionar el mismo archivo de nuevo si falló
          if (this.$refs.fileInput) this.$refs.fileInput.value = null;
        }
      };
      reader.readAsArrayBuffer(file);
    },

    procesarJSONExcel(rows) {
      const tareasProcesadas = [];
      let hayAdvertencia = false;
      const idCicloDeseado = this.importDialog.id_ciclo || this.filtros.ciclo || null;

      // Diccionarios en lowercase para búsqueda rápida en el Front
      const lPlatforms = this.plataformas.map(p => p.nombre.trim().toLowerCase());
      const lFormats = this.formatos.map(f => f.nombre.trim().toLowerCase());
      const lTemas = this.temas.map(t => t.nombre.trim().toLowerCase());
      const lEstados = this.estados.map(e => e.nombre.trim().toLowerCase());

      rows.forEach(row => {
        const plat = (row['Plataforma'] || '').toString().trim();
        const form = (row['Formato'] || '').toString().trim();
        const tema = (row['Tema'] || '').toString().trim();
        const desc = (row['Descripción del Post / Tarea'] || '').toString().trim();
        const est = (row['Estado'] || 'Pendiente').toString().trim();
        const fechaVal = row['Fecha'];

        if (!plat && !form && !tema) return; // Fila vacía

        if (!lPlatforms.includes(plat.toLowerCase()) ||
          !lFormats.includes(form.toLowerCase()) ||
          !lTemas.includes(tema.toLowerCase()) ||
          !lEstados.includes(est.toLowerCase())) {
          hayAdvertencia = true;
        }

        // Parseo de fecha de Excel (Número serial a Date)
        let fechaFormat = null;
        if (fechaVal) {
          if (typeof fechaVal === 'number') {
            const excelDate = new Date(Math.round((fechaVal - 25569) * 86400 * 1000));
            fechaFormat = moment(excelDate).format('YYYY-MM-DD');
          } else {
            fechaFormat = moment(fechaVal, ['YYYY-MM-DD', 'DD/MM/YYYY', 'MM/DD/YYYY']).format('YYYY-MM-DD');
          }
        }

        tareasProcesadas.push({
          id_ciclo: idCicloDeseado, // <- Se sobrescribirá luego en ejecutarImportacion (reactive delay)
          fecha: fechaFormat,
          plataforma_nombre: plat,
          formato_nombre: form,
          tema_nombre: tema,
          descripcion: desc,
          estado_nombre: est
        });
      });

      this.importDialog.tareas = tareasProcesadas;
      this.importDialog.advertencia = hayAdvertencia;
      this.importDialog.id_ciclo = idCicloDeseado;
      this.dlgImportar = true;
    },

    async ejecutarImportacion() {
      if (!this.importDialog.id_ciclo) return this.showSnack('Te faltó seleccionar un ciclo', 'warning');
      this.guardando = true;

      // Repasar el array con el id del modal (en caso de que lo haya cambiado en UI)
      const tasksToPush = this.importDialog.tareas.map(t => ({ ...t, id_ciclo: this.importDialog.id_ciclo }));

      try {
        await this.$http.post('checklist.tareas.import', {
          instancia: this.instancia,
          id_usuario: this.idUsuario,
          tareas: tasksToPush
        });
        this.showSnack('Importación exitosa. Cargando...');
        this.dlgImportar = false;

        // Recargar catálogos por si se inyectaron nuevos elementos on-the-fly
        await this.cargarCatalogosEInit();
      } catch (error) {
        this.showSnack((error.response && error.response.data && error.response.data.message) || 'Error importando', 'error');
      } finally {
        this.guardando = false;
        this.$refs.fileInput.value = null; // Reiniciar DOM file input
      }
    },

    // ==========================================
    // EDICIÓN / ALTA MANUAL (UI y Handlers)
    // ==========================================
    abrirAgregarManual() {
      this.esNuevaTarea = true;
      this.itemEdit = null;
      let pdEstado = this.estados.find(e => e.nombre.toLowerCase() == 'pendiente') || (this.estados[0] || {});
      this.formModel = {
        id_ciclo: this.filtros.ciclo || (this.ciclosVisibles[0] ? this.ciclosVisibles[0].id_ciclo : null),
        fecha: moment().format('YYYY-MM-DD'),
        id_plataforma: this.plataformas[0] ? this.plataformas[0].id : null,
        id_tipo: this.formatos[0] ? this.formatos[0].id : null,
        id_tema: this.temas[0] ? this.temas[0].id : null,
        id_estado: pdEstado.id,
        descripcion: ''
      };
      this.dlgEdit = true;
    },

    abrirEdicion(item) {
      this.esNuevaTarea = false;
      this.itemEdit = { ...item };
      this.formModel = {
        id_ciclo: item.id_ciclo,
        fecha: item.fecha,
        id_plataforma: item.id_plataforma,
        id_tipo: item.id_tipo,
        id_tema: item.id_tema,
        id_estado: item.id_estado,
        descripcion: item.descripcion
      }
      this.dlgEdit = true;
    },

    async guardarEdicion() {
      if (this.esNuevaTarea && !this.formModel.id_ciclo) return this.showSnack('Falta indicar el Ciclo', 'warning');
      this.guardando = true;
      try {
        if (this.esNuevaTarea) {
          await this.$http.post(`checklist.tareas.add`, {
            instancia: this.instancia,
            id_usuario: this.idUsuario,
            ...this.formModel
          });
          this.showSnack('Nueva Tarea Agregada');
        } else {
          await this.$http.put(`checklist.tareas.admin.update/${this.itemEdit.id}`, {
            instancia: this.instancia,
            puestosPermitidos: true,
            ...this.formModel,
            id_usuario: this.idUsuario
          });
          this.showSnack('Tarea actualizada');
        }
        this.dlgEdit = false;
        this.cargarTareas();
      } catch (e) {
        this.showSnack('Error al guardar', 'error');
      } finally {
        this.guardando = false;
      }
    },

    eliminarTarea(item) {
      this.$swal.fire({
        title: "¿Seguro de borrar esta actividad?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        confirmButtonText: "Sí, borrar"
      }).then(async (res) => {
        if (res.isConfirmed) {
          try {
            await this.$http.put(`checklist.tareas.admin.delete/${item.id}`, {
              instancia: this.instancia,
              id_usuario: this.idUsuario,
              puestosPermitidos: true
            });
            this.showSnack('Eliminada correctamente');
            this.cargarTareas();
          } catch (e) {
            this.showSnack('Error al eliminar', 'error');
          }
        }
      });
    },

    // ==========================================
    // VINCULACIÓN DE RECURSOS (MULTIMEDIA)
    // ==========================================
    async abrirVincularModal(item) {
      this.tareaVincular = item;
      this.recursoSeleccionado = null;
      // Cargar los recursos del módulo multimedia que estén en el mismo ciclo
      try {
        const res = await this.$http.get(`multimedia.recursos.list?inst=${this.instancia}&ciclo=${this.filtros.ciclo || ''}`);
        this.recursosLocales = res.data;
        this.dlgVincular = true;
      } catch (err) {
        this.showSnack('Error obteniendo recursos multimedia', 'error');
      }
    },

    async confirmarVinculacion() {
      this.guardando = true;
      try {
        await this.$http.put(`checklist.tareas.vincular/${this.tareaVincular.id}`, {
          instancia: this.instancia,
          id_usuario: this.idUsuario,
          id_recurso: this.recursoSeleccionado
        });
        this.showSnack('Vínculo exitoso');
        this.dlgVincular = false;
        this.cargarTareas();
      } catch (e) {
        this.showSnack('Error al vincular', 'error');
      } finally {
        this.guardando = false;
      }
    },

    async desvincularRecurso(item) {
      this.$swal.fire({
        title: "¿Desvincular archivo?",
        text: "Quitarás la relación, pero el archivo seguirá existiendo en el listado de recursos multimedia.",
        icon: "question",
        showCancelButton: true,
        confirmButtonColor: "#f39c12",
        confirmButtonText: "Desvincular"
      }).then(async (res) => {
        if (res.isConfirmed) {
          try {
            await this.$http.put(`checklist.tareas.vincular/${item.id}`, {
              instancia: this.instancia,
              id_usuario: this.idUsuario,
              id_recurso: null
            });
            this.showSnack('Recurso desvinculado. El estado regresó a Pendiente.');
            this.cargarTareas();
          } catch (e) {
            this.showSnack('Error al desvincular', 'error');
          }
        }
      });
    },

    verRecursoVinculado(item) {
      this.recursoVisor = item;
      if (this.esPdf(item.recurso_url)) {
        this.abrirArchivoPestana(item.recurso_url);
      } else {
        this.dlgVisor = true;
      }
    }
  }
};
</script>
